import { getAuth, signInAnonymously, User as FirebaseUser } from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { User, StudentSession } from '../../types';

export class StudentAuthService {
  private static readonly SESSION_COLLECTION = 'studentSessions';
  
  /**
   * Authenticate a student using Firebase Anonymous Auth with session mapping
   */
  static async authenticateStudent(studentId: string, squadId: string): Promise<{ firebaseUser: FirebaseUser; studentData: User }> {
    console.log('🔐 StudentAuthService.authenticateStudent called', { studentId, squadId });
    
    const auth = getAuth();
    console.log('📱 Current auth state before anonymous signin:', {
      currentUser: auth.currentUser?.uid,
      isAnonymous: auth.currentUser?.isAnonymous
    });
    
    // Sign in anonymously to get a Firebase user
    console.log('🔑 Attempting anonymous sign in...');
    const userCredential = await signInAnonymously(auth);
    const anonymousUser = userCredential.user;
    console.log('✅ Anonymous sign in successful:', {
      uid: anonymousUser.uid,
      isAnonymous: anonymousUser.isAnonymous
    });
    
    // Create session mapping between anonymous UID and real student data
    const sessionData: StudentSession = {
      studentId,
      squadId,
      loginTime: serverTimestamp() as any,
      active: true,
    };
    
    console.log('💾 Creating session mapping...', {
      sessionCollection: this.SESSION_COLLECTION,
      anonymousUid: anonymousUser.uid,
      sessionData
    });
    
    try {
      // Store the session mapping in Firestore
      await setDoc(doc(db, this.SESSION_COLLECTION, anonymousUser.uid), sessionData);
      console.log('✅ Session mapping created successfully');
    } catch (error) {
      console.error('❌ Failed to create session mapping:', error);
      throw error;
    }
    
    // Note: We're not updating squad's online students list here because
    // anonymous users don't have permission to update squad documents.
    // This can be handled server-side via a Cloud Function trigger if needed.
    console.log('ℹ️ Skipping squad online students update (requires teacher permissions)');
    
    // Store student info in localStorage for quick access
    const localData = {
      anonymousUid: anonymousUser.uid,
      studentId,
      squadId,
      loginTime: new Date().toISOString(),
    };
    localStorage.setItem('studentAuth', JSON.stringify(localData));
    console.log('💿 Local storage updated:', localData);
    
    // Fetch student data to return it directly
    const studentDoc = await getDoc(doc(db, 'users', studentId));
    if (!studentDoc.exists()) {
      console.error('❌ Student document not found right after authentication:', studentId);
      throw new Error('Could not find student data after authentication.');
    }
    const studentData = { id: studentDoc.id, ...studentDoc.data() } as User;
    
    console.log(`🎉 Student ${studentId} authenticated via anonymous auth (${anonymousUser.uid})`);

    return { firebaseUser: anonymousUser, studentData };
  }
  
  /**
   * Get the real student data for the current anonymous user
   */
  static async getCurrentStudentData(): Promise<User | null> {
    console.log('👤 StudentAuthService.getCurrentStudentData called');
    
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    console.log('📱 Current auth state:', {
      hasUser: !!currentUser,
      uid: currentUser?.uid,
      isAnonymous: currentUser?.isAnonymous
    });
    
    if (!currentUser || !currentUser.isAnonymous) {
      console.log('❌ No anonymous user found');
      return null;
    }
    
    try {
      // First try to get from localStorage for immediate access
      const localData = this.getLocalStudentInfo();
      if (localData && localData.anonymousUid === currentUser.uid) {
        console.log('📱 Found matching local student info:', localData);
        
        // Get real student data using the cached student ID
        console.log('👨‍🎓 Fetching student data from cache...', { studentId: localData.studentId });
        const studentDoc = await getDoc(doc(db, 'users', localData.studentId));
        
        if (studentDoc.exists()) {
          const studentData = { id: studentDoc.id, ...studentDoc.data() } as User;
          console.log('✅ Student data retrieved from cache:', {
            id: studentData.id,
            displayName: studentData.displayName,
            role: studentData.role
          });
          return studentData;
        }
      } else if (localData) {
        console.log('🗑️ Clearing stale localStorage data:', {
          cachedUid: localData.anonymousUid,
          currentUid: currentUser.uid
        });
        localStorage.removeItem('studentAuth');
      }
      
      // Fallback: Get session mapping from Firestore
      console.log('🔍 Looking up session mapping...', {
        collection: this.SESSION_COLLECTION,
        docId: currentUser.uid
      });
      const sessionDoc = await getDoc(doc(db, this.SESSION_COLLECTION, currentUser.uid));
      
      if (!sessionDoc.exists()) {
        console.log('❌ No session mapping found');
        return null;
      }
      
      const session = sessionDoc.data() as StudentSession;
      console.log('✅ Session mapping found:', session);
      
      // Get real student data
      console.log('👨‍🎓 Fetching student data...', { studentId: session.studentId });
      const studentDoc = await getDoc(doc(db, 'users', session.studentId));
      
      if (!studentDoc.exists()) {
        console.log('❌ Student document not found');
        return null;
      }
      
      const studentData = { id: studentDoc.id, ...studentDoc.data() } as User;
      console.log('✅ Student data retrieved:', {
        id: studentData.id,
        displayName: studentData.displayName,
        role: studentData.role
      });
      
      return studentData;
    } catch (error) {
      console.error('❌ Error in getCurrentStudentData:', error);
      return null;
    }
  }
  
  /**
   * Check if current user is an authenticated student
   */
  static async isAuthenticatedStudent(): Promise<boolean> {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser || !currentUser.isAnonymous) {
      return false;
    }
    
    const sessionDoc = await getDoc(doc(db, this.SESSION_COLLECTION, currentUser.uid));
    return sessionDoc.exists();
  }
  
  /**
   * Sign out student and clean up session
   */
  static async signOut(): Promise<void> {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (currentUser && currentUser.isAnonymous) {
      // Get session data before signing out
      const sessionDoc = await getDoc(doc(db, this.SESSION_COLLECTION, currentUser.uid));
      
      if (sessionDoc.exists()) {
        // Mark session as inactive
        await updateDoc(doc(db, this.SESSION_COLLECTION, currentUser.uid), {
          active: false,
          logoutTime: serverTimestamp(),
        });
        
        // Note: Not removing from squad's online students list here
        // because anonymous users don't have update permissions on squads
        console.log('ℹ️ Session marked as inactive, squad update skipped');
      }
    }
    
    // Clear localStorage
    localStorage.removeItem('studentAuth');
    
    // Sign out from Firebase
    await auth.signOut();
  }
  
  /**
   * Get student info from localStorage (quick access)
   */
  static getLocalStudentInfo(): { anonymousUid: string; studentId: string; squadId: string; loginTime: string } | null {
    const stored = localStorage.getItem('studentAuth');
    return stored ? JSON.parse(stored) : null;
  }
  
  /**
   * Clear all student authentication data (for debugging/cleanup)
   */
  static clearAllData(): void {
    localStorage.removeItem('studentAuth');
    console.log('🗑️ All student auth data cleared');
  }
} 